import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {ModalHandle, ModalOptions, ModalOutputs} from '@core/types';
import {ModalRef} from '@core/utils';

const DEFAULT_OPTIONS: Required<ModalOptions> = {
  closeOnBackdrop: true,
  closeOnEsc: true,
  showCloseButton: true,
  size: 'md',
  ariaLabel: 'Modal dialog',
};

@Injectable({providedIn: 'root'})
export class ModalService {
  private readonly _stack$ = new BehaviorSubject<ModalHandle[]>([]);
  readonly stack$ = this._stack$.asObservable();
  private readonly _refs = new Map<string, ModalRef<any>>();

  open<TComponent extends object, TResult = unknown>(
    component: any,
    config?: {
      inputs?: Partial<TComponent>;
      outputs?: ModalOutputs<TComponent>;
      options?: ModalOptions;
    }
  ): ModalRef<TResult> {
    const id = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

    const ref = new ModalRef<TResult>(id, (result?: TResult) => this.close(id, result));

    const handle: ModalHandle<TComponent> = {
      id,
      component,
      inputs: config?.inputs ?? {},
      outputs: config?.outputs ?? undefined,
      options: {...DEFAULT_OPTIONS, ...(config?.options ?? {})},
      isClosing: false,
    };

    this._stack$.next([...this._stack$.value, handle]);
    this._refs.set(id, ref);

    return ref;
  }

  close<TResult = unknown>(id: string, result?: TResult) {
    const stack = this._stack$.value;
    const idx = stack.findIndex(m => m.id === id);
    if (idx < 0) return;

    const updated = stack.map(m => (m.id === id ? {...m, isClosing: true} : m));
    this._stack$.next(updated);

    window.setTimeout(() => {
      const ref = this._refs.get(id) as ModalRef<TResult> | undefined;
      if (ref) ref._complete(result);

      this._refs.delete(id);
      this._stack$.next(this._stack$.value.filter(m => m.id !== id));
    }, 180);
  }

  closeTop<TResult = unknown>(result?: TResult) {
    const stack = this._stack$.value;
    const top = stack[stack.length - 1];
    if (top) this.close<TResult>(top.id, result);
  }
}
